<script setup>
import { computed } from 'vue';

const props = defineProps({
    title: {
        type: String,
        required: true,
    },
    value: {
        type: [String, Number],
        required: true,
    },
    details: {
        type: Array,
        default: () => [],
    },
    isLoading: {
        type: Boolean,
        default: false,
    },
    layout: {
        type: String,
        default: 'compact',
    },
    bgClass: {
        type: String,
        default: 'bg-n-solid-2 dark:bg-n-background',
    },
    textClass: {
        type: String,
        default: 'text-n-slate-11',
    },
    titleClass: {
        type: String,
        default: 'text-n-slate-12',
    },
});

const formatValue = computed(() => {
    if (typeof props.value === 'number') {
        return props.value.toLocaleString();
    }
    return props.value;
});

const hasDetails = computed(() => props.details.length > 0);
</script>

<template>
    <div
        :class="[
            bgClass,
            'rounded-xl p-6 transition-colors duration-150 flex flex-col outline outline-1 outline-n-container hover:shadow-md dark:bg-n-vars-black-br/30',
            {
                'gap-6': layout === 'compact',
                'gap-4': layout === 'spacious'
            }
        ]"
    >
        <div v-if="isLoading" class="flex items-center justify-center h-32">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-300 opacity-50"></div>
        </div>

        <div v-else class="flex flex-col flex-1 gap-4">
            <div
                class="flex justify-between items-start"
                :class="{
                    'min-h-[48px]': layout === 'compact'
                }"
            >
                <h3 :class="[titleClass, 'text-xs lg:text-base font-semibold']">
                    {{ title }}
                </h3>
            </div>

            <div :class="[titleClass, 'font-bold text-4xl']">
                {{ formatValue }}
            </div>

            <div
                v-if="hasDetails"
                class="mt-auto"
                :class="{
                    'flex flex-col gap-[3px] md:flex-row md:gap-2': layout === 'spacious',
                    'flex flex-col gap-[3px] md:flex-row md:justify-between lg:flex-col': layout === 'compact'
                }"
            >
                <div v-for="(detail, index) in details" :key="index" class="flex items-center justify-start gap-2 h-5">
                    <span :class="[textClass, 'text-xs lg:text-sm whitespace-nowrap']">{{ detail.label }}</span>
                    <span class="text-green-500 text-sm font-medium">
                        {{ typeof detail.value === 'number' ? detail.value.toLocaleString() : detail.value }}
                    </span>
                </div>
            </div>

            <div v-if="!hasDetails" class="text-slate-400 text-sm mt-auto">
                {{ $t('REPORT.EMPTY_STATE.MESSAGE') }}
            </div>
        </div>
    </div>
</template>